// @dynamic-i18n-keys
//   offerBanner.endsCountdown
//   offerBanner.endsCountdown.mobile
//   offerBanner.startingPrice
//   offerBanner.startingPrice.mobile
//   offerBanner.highestOffer
//   offerBanner.highestOffer.mobile
//   offerBanner.myOffer
//   offerBanner.myOffer.mobile
//   offerBanner.numOffers
//   offerBanner.numOffers.mobile
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import numeral from 'numeral';
import { PapiAdoptable, PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import SaleIcon from '@wix/da-ds/pkg/Icons/24x24/Sale';
import Countdown from '@wix/da-ds/pkg/Time/Countdown';
import { formatDollars } from '../../../utils/formatting';

import s from './OfferBanner.scss';

export interface ClassNameProp {
  className?: string;
  deviation?: PapiDeviation;
  onEnd?: () => void;
}
type Props = ClassNameProp & Partial<PapiAdoptable['offers']>;

/** Adoptables buyer/offer banner */
export const OfferBanner: React.FC<Props> = ({
  offerEndTime,
  highestOffer,
  minOffer,
  ownOffer,
  numOffers,
  className,
  deviation,
  onEnd,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const mobileSuffix = isMobile ? '.mobile' : '';

  if (!offerEndTime) {
    return null;
  }
  const ownOfferDollarPrice = ownOffer?.dollarPrice;

  return (
    <div className={classnames(s['root'], className)}>
      <SaleIcon className={s['icon']} size={IconSize.MEDIUM} />

      <span className={s['offer-info']}>
        <Countdown
          prefix={t(`offerBanner.endsCountdown${mobileSuffix}`)}
          countToDate={offerEndTime}
          renderTimer={renderTimer}
          onEnd={onEnd}
          endedMessage={t('offerBanner.saleEnded')}
        />
      </span>

      {!!minOffer && (
        <span className={s['offer-info']}>
          {t(`offerBanner.startingPrice${mobileSuffix}`, {
            postProcess: ['reactPostprocessor', 'featureBrancher'],
            price: <b>{formatDollars(minOffer)}</b>,
          })}
        </span>
      )}

      {!!highestOffer && (
        <span className={s['offer-info']}>
          {t(`offerBanner.highestOffer${mobileSuffix}`, {
            postProcess: ['reactPostprocessor', 'featureBrancher'],
            price: <b>{formatDollars(highestOffer)}</b>,
          })}
        </span>
      )}

      {!!ownOfferDollarPrice && (
        <span className={s['offer-info']}>
          {t(`offerBanner.myOffer${mobileSuffix}`, {
            postProcess: ['reactPostprocessor', 'featureBrancher'],
            price: <b>{formatDollars(ownOfferDollarPrice)}</b>,
          })}
        </span>
      )}

      {!ownOffer && !!numOffers && (
        <span className={s['offer-info']}>
          {t(`offerBanner.numOffers${mobileSuffix}`, {
            postProcess: ['reactPostprocessor', 'featureBrancher'],
            offers: <b>{`${numOffers}`}</b>,
          })}
        </span>
      )}
    </div>
  );
};

export default OfferBanner;

// ----

function renderTimer(d, h, m, sec) {
  const hasEnded = d + h + m + sec <= 0;
  const countdownString = [d, h, m, sec].map(n => numeral(n).format('00')).join(':');
  const classNames = classnames(s['countdown'], hasEnded && s['countdown-ended']);
  return <span className={classNames}>{countdownString}</span>;
}
